import React, { createContext, useContext, useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

const MapProviderContext = createContext();

const MAP_PROVIDER_KEY = '@chessmaps_map_provider';
const MAP_3D_KEY = '@chessmaps_map_3d';
const MAP_STREET_LABELS_KEY = '@chessmaps_map_street_labels';

// Available map providers
export const MAP_PROVIDERS = {
  GOOGLE: 'google',
  MAPBOX: 'mapbox',
};

export const useMapProvider = () => {
  const context = useContext(MapProviderContext);
  if (!context) {
    throw new Error('useMapProvider must be used within a MapProviderProvider');
  }
  return context;
};

// Street labels hidden by default
const DEFAULT_SHOW_STREET_LABELS = false;

export const MapProviderProvider = ({ children }) => {
  const [mapProvider, setMapProviderState] = useState(MAP_PROVIDERS.GOOGLE);
  const [is3DEnabled, setIs3DEnabledState] = useState(true); // 3D enabled by default
  const [showStreetLabels, setShowStreetLabelsState] = useState(DEFAULT_SHOW_STREET_LABELS);
  const [isLoading, setIsLoading] = useState(true);

  // Load saved preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const [savedProvider, saved3D, savedStreetLabels] = await Promise.all([
        AsyncStorage.getItem(MAP_PROVIDER_KEY),
        AsyncStorage.getItem(MAP_3D_KEY),
        AsyncStorage.getItem(MAP_STREET_LABELS_KEY),
      ]);
      
      if (savedProvider !== null) {
        setMapProviderState(savedProvider);
      }
      if (saved3D !== null) {
        setIs3DEnabledState(saved3D === 'true');
      }
      if (savedStreetLabels !== null) {
        setShowStreetLabelsState(savedStreetLabels === 'true');
      }
    } catch (error) {
      console.error('Error loading map preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setMapProvider = async (provider) => {
    try {
      setMapProviderState(provider);
      await AsyncStorage.setItem(MAP_PROVIDER_KEY, provider);
      console.log('🗺️ Map provider changed to:', provider);
    } catch (error) {
      console.error('Error saving map provider preference:', error);
    }
  };

  const toggleMapProvider = async () => {
    const newProvider = mapProvider === MAP_PROVIDERS.GOOGLE 
      ? MAP_PROVIDERS.MAPBOX 
      : MAP_PROVIDERS.GOOGLE;
    await setMapProvider(newProvider);
  };

  const setIs3DEnabled = async (enabled) => {
    try {
      setIs3DEnabledState(enabled);
      await AsyncStorage.setItem(MAP_3D_KEY, String(enabled));
      console.log('🏔️ 3D mode changed to:', enabled);
    } catch (error) {
      console.error('Error saving 3D preference:', error);
    }
  };

  const toggle3D = async () => {
    await setIs3DEnabled(!is3DEnabled);
  };

  const setShowStreetLabels = async (enabled) => {
    try {
      setShowStreetLabelsState(enabled);
      await AsyncStorage.setItem(MAP_STREET_LABELS_KEY, String(enabled));
      console.log('🗺️ Street labels on map:', enabled ? 'visible' : 'hidden');
    } catch (error) {
      console.error('Error saving street labels preference:', error);
    }
  };

  const toggleStreetLabels = async () => {
    await setShowStreetLabels(!showStreetLabels);
  };

  return (
    <MapProviderContext.Provider 
      value={{ 
        mapProvider, 
        setMapProvider, 
        toggleMapProvider,
        is3DEnabled,
        setIs3DEnabled,
        toggle3D,
        showStreetLabels,
        setShowStreetLabels,
        toggleStreetLabels,
        isLoading,
        isGoogle: mapProvider === MAP_PROVIDERS.GOOGLE,
        isMapbox: mapProvider === MAP_PROVIDERS.MAPBOX,
      }}
    >
      {children}
    </MapProviderContext.Provider>
  );
};



